import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Grid from "./Grid";

/**
 * Portals into #root (or document.body as fallback) so Tailwind CSS vars
 * are always in scope. Backdrop is very light — no blur.
 */
const SectionModal = ({ section, currentCompany, baseMargin, onClose }) => {
  const isOpen = !!section;
  const panelRef = useRef(null);
  const [portalTarget, setPortalTarget] = useState(null);

  // Find the app root so CSS vars / Tailwind theme are inherited
  useEffect(() => {
    setPortalTarget(document.getElementById("root") || document.body);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  // Don't lock body scroll — let the page stay interactive (sidebar can open)
  useEffect(() => {
    if (isOpen) panelRef.current?.focus();
  }, [isOpen]);

  if (!portalTarget) return null;

  return createPortal(
    <>
      {/* Backdrop — very light, no blur */}
      <div
        aria-hidden
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 9998,
          background: "rgba(0,0,0,0.25)",
          transition: "opacity 0.15s ease",
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? "auto" : "none",
        }}
      />

      {/* Centering shell — pointer-events off so backdrop fires */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px 16px",
          pointerEvents: "none",
        }}
      >
        {/* Panel — same visual language as overview section cards */}
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-label={section ?? undefined}
          tabIndex={-1}
          className="bg-surface/80 border border-surfaceBorder backdrop-blur-md font-poppins font-normal text-mainText outline-none flex flex-col w-full shadow-login-panel"
          style={{
            borderRadius: 8,
            maxWidth: 680,
            height: "82vh",
            maxHeight: "82vh",
            transition: "opacity 0.15s ease, transform 0.15s ease",
            opacity: isOpen ? 1 : 0,
            transform: isOpen
              ? "translateY(0) scale(1)"
              : "translateY(6px) scale(0.99)",
            pointerEvents: isOpen ? "auto" : "none",
          }}
        >
          {/* Header */}
          <div
            className="flex shrink-0 items-center justify-between"
            style={{ padding: "14px 18px 0" }}
          >
            <h2
              className="text-xs font-normal uppercase text-mutedText"
              style={{ letterSpacing: "0.08em" }}
            >
              {section}
            </h2>
            <button
              onClick={onClose}
              aria-label="Close"
              className="flex h-6 w-6 items-center justify-center rounded-md text-mutedText transition-colors hover:bg-innerBg hover:text-mainText focus-visible:outline-none"
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Scrollable body */}
          <div
            className="flex-1 min-h-0 overflow-y-auto"
            style={{ padding: "0 18px 16px" }}
          >
            {isOpen && section && (
              <Grid
                tileType={section}
                currentCase={currentCompany}
                baseMargin={0}
                onBack={onClose}
                hideBack
                noFixedHeight
              />
            )}
          </div>
        </div>
      </div>
    </>,
    portalTarget,
  );
};

export default SectionModal;
